<template>
    <v-container :fluid="true" class="max-height">
        <!-- Header -->
        <v-row class="mb-4">
            <v-col cols="12" class="d-flex justify-space-between align-center px-5">
                <div>
                    <h1 class="text-h4 font-weight-bold">Dispatch</h1>
                    <p class="text-body-2 text-grey-darken-1 mt-1">
                        Build a day's stops and give it to a driver. They see it once it is published.
                    </p>
                </div>
                <v-btn color="primary" prepend-icon="mdi-plus" size="large" rounded="lg" elevation="2"
                       @click="openRouteDialog()">
                    New route
                </v-btn>
            </v-col>
        </v-row>

        <!-- Filters -->
        <v-row class="px-5 mb-2">
            <v-col cols="12" md="3">
                <v-text-field v-model="date" type="date" density="compact" variant="outlined"
                              label="Date" hide-details rounded="lg" @update:model-value="datePicked = true"/>
            </v-col>
            <v-col cols="12" md="3">
                <v-select v-model="driverFilter" :items="driverOptions" item-title="title" item-value="value"
                          density="compact" variant="outlined" label="Driver" clearable hide-details rounded="lg"/>
            </v-col>
            <v-col cols="12" md="3">
                <v-select v-model="statusFilter" :items="statusOptions" density="compact" variant="outlined"
                          label="Status" clearable hide-details rounded="lg"/>
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center justify-end">
                <v-btn variant="text" prepend-icon="mdi-refresh" :loading="loading" @click="loadRoutes">Refresh</v-btn>
            </v-col>
        </v-row>

        <v-row class="px-5">
            <v-col cols="12">
                <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3"/>

                <v-alert v-if="!loading && routes.length === 0" type="info" variant="tonal" rounded="lg">
                    No routes for this day. Create one and add the stops the driver has to make.
                </v-alert>

                <v-card v-for="route in routes" :key="route.id" class="mb-4" rounded="lg" elevation="2">
                    <v-card-title class="d-flex align-center flex-wrap ga-2">
                        <span class="font-weight-bold">{{ route.label || 'Route ' + route.id }}</span>
                        <v-chip :color="statusColour(route.status)" size="small" label>{{ route.status }}</v-chip>
                        <v-chip v-if="route.driver" size="small" variant="tonal" prepend-icon="mdi-account">
                            {{ driverName(route.driver) }}
                        </v-chip>
                        <v-chip v-else size="small" variant="tonal" color="warning">Unassigned</v-chip>
                        <v-chip v-if="route.asset" size="small" variant="tonal" prepend-icon="mdi-truck">
                            {{ route.asset.label }}
                        </v-chip>
                        <v-spacer/>
                        <span class="text-body-2 text-grey-darken-1">{{ progress(route) }}</span>
                    </v-card-title>

                    <v-card-text>
                        <p v-if="route.notes" class="text-body-2 mb-3">{{ route.notes }}</p>

                        <v-table density="compact">
                            <thead>
                            <tr>
                                <th style="width: 56px">#</th>
                                <th>Stop</th>
                                <th>Window</th>
                                <th>Outcome</th>
                                <th style="width: 120px"/>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(stop, stopIndex) in route.stops" :key="stop.id">
                                <td>{{ stopIndex + 1 }}</td>
                                <td>
                                    <div class="font-weight-medium">{{ stop.label }}</div>
                                    <div class="text-caption text-grey-darken-1">{{ stop.address }}</div>
                                    <div v-if="stop.reference" class="text-caption">Ref {{ stop.reference }}</div>
                                </td>
                                <td class="text-caption">{{ windowLabel(stop) }}</td>
                                <td>
                                    <v-chip :color="stopColour(stop.status)" size="x-small" label>{{ stop.status }}</v-chip>
                                    <div v-if="stop.arrived_at" class="text-caption">
                                        arrived {{ formatDateTime(stop.arrived_at) }}
                                    </div>
                                    <div v-if="stop.failure_reason" class="text-caption text-error">
                                        {{ stop.failure_reason }}
                                    </div>
                                    <div v-if="stop.disposition" class="text-caption">
                                        {{ stop.disposition.replaceAll('_', ' ') }}
                                        <span v-if="stop.edi_status_code">({{ stop.edi_status_code }}<span
                                            v-if="stop.edi_reason_code">/{{ stop.edi_reason_code }}</span>)</span>
                                    </div>
                                </td>
                                <td class="text-right">
                                    <v-btn icon="mdi-arrow-up" size="x-small" variant="text"
                                           :disabled="stopIndex === 0" @click="move(route, stop, -1)"/>
                                    <v-btn icon="mdi-arrow-down" size="x-small" variant="text"
                                           :disabled="stopIndex === route.stops.length - 1" @click="move(route, stop, 1)"/>
                                    <v-btn icon="mdi-pencil" size="x-small" variant="text" @click="openStopDialog(route, stop)"/>
                                    <v-btn icon="mdi-delete" size="x-small" variant="text" color="error"
                                           @click="removeStop(stop)"/>
                                </td>
                            </tr>
                            <tr v-if="route.stops.length === 0">
                                <td colspan="5" class="text-center text-grey-darken-1 py-4">No stops yet.</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-card-text>

                    <v-card-actions class="px-4 pb-4">
                        <v-btn prepend-icon="mdi-map-marker-plus" variant="tonal" @click="openStopDialog(route)">
                            Add stop
                        </v-btn>
                        <v-btn v-if="route.status === 'draft'" color="primary" prepend-icon="mdi-send"
                               :disabled="route.stops.length === 0 || !route.driver_id"
                               @click="setStatus(route, 'published')">
                            Publish to driver
                        </v-btn>
                        <v-btn v-else-if="route.status === 'published'" variant="text" prepend-icon="mdi-pencil-off"
                               @click="setStatus(route, 'draft')">
                            Unpublish
                        </v-btn>
                        <v-spacer/>
                        <v-btn variant="text" prepend-icon="mdi-pencil" @click="openRouteDialog(route)">Edit</v-btn>
                        <v-btn variant="text" color="error" prepend-icon="mdi-delete" @click="removeRoute(route)">
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <!-- Route dialog -->
        <v-dialog v-model="routeDialog" max-width="560">
            <v-card rounded="lg">
                <v-card-title>{{ routeForm.id ? 'Edit route' : 'New route' }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="routeForm.route_date" type="date" label="Date" variant="outlined"
                                  density="comfortable" class="mb-3"/>
                    <v-select v-model="routeForm.driver_id" :items="driverOptions" item-title="title"
                              item-value="value" label="Driver" variant="outlined" density="comfortable"
                              clearable class="mb-3"/>
                    <v-select v-model="routeForm.asset_id" :items="assetOptions" item-title="title"
                              item-value="value" label="Vehicle" variant="outlined" density="comfortable"
                              clearable class="mb-3"/>
                    <v-text-field v-model="routeForm.label" label="Label" variant="outlined"
                                  density="comfortable" placeholder="Monday run" class="mb-3"/>
                    <v-textarea v-model="routeForm.notes" label="Notes for the driver" variant="outlined"
                                density="comfortable" rows="2"/>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="routeDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="saving" @click="saveRoute">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Stop dialog -->
        <v-dialog v-model="stopDialog" max-width="620">
            <v-card rounded="lg">
                <v-card-title>{{ stopForm.id ? 'Edit stop' : 'Add stop' }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="stopForm.label" label="Customer or place" variant="outlined"
                                  density="comfortable" class="mb-3"/>
                    <v-text-field v-model="stopForm.address" label="Address" variant="outlined"
                                  density="comfortable" class="mb-3"/>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.lat" label="Latitude" variant="outlined"
                                          density="comfortable" type="number"/>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.lng" label="Longitude" variant="outlined"
                                          density="comfortable" type="number"/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.window_start" label="Window from" type="datetime-local"
                                          variant="outlined" density="comfortable" :hint="zoneHint" persistent-hint/>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.window_end" label="Window to" type="datetime-local"
                                          variant="outlined" density="comfortable" :hint="zoneHint" persistent-hint/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="6">
                            <v-select v-model="stopForm.type" :items="['delivery', 'pickup', 'service']"
                                      label="Type" variant="outlined" density="comfortable"/>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.reference" label="Reference" variant="outlined"
                                          density="comfortable" placeholder="PO 44821"/>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.contact_name" label="Contact" variant="outlined"
                                          density="comfortable"/>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field v-model="stopForm.contact_phone" label="Contact phone" variant="outlined"
                                          density="comfortable"/>
                        </v-col>
                    </v-row>
                    <v-textarea v-model="stopForm.notes" label="Note for the driver" variant="outlined"
                                density="comfortable" rows="2" placeholder="Dock 12, ring the bell"/>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn variant="text" @click="stopDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="saving" @click="saveStop">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {send} from "@/composables/apiClient";
import {useRoute} from "vue-router";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatCompanyDateTime, formatDateTime} from "@/composables/useTimezone";

const {showSnackbar} = useSnackbarStore();

/**
 * Today as YYYY-MM-DD in a zone (the browser's when none is given). The board
 * starts on the browser's day and moves to the company's as soon as the API
 * names its zone; a UTC date showed tomorrow's board from 8pm in New York.
 */
function todayIn(zone) {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            year: 'numeric', month: '2-digit', day: '2-digit', timeZone: zone || undefined,
        }).format(new Date());
    } catch {
        return new Intl.DateTimeFormat('en-CA', {year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date());
    }
}

/** The zone the API reads and shows this company's windows in. */
const boardZone = ref(null);
// ?date= from the menu count: that day's board, every driver and status.
const pageRoute = useRoute();
const queryDate = () => (/^\d{4}-\d{2}-\d{2}$/.test(pageRoute.query.date ?? '') ? pageRoute.query.date : null);
const datePicked = ref(!!queryDate());
const date = ref(queryDate() ?? todayIn());
const driverFilter = ref(null);
const statusFilter = ref(null);
watch(() => pageRoute.query.date, () => {
    if (pageRoute.name !== 'fleet-dispatch') return;
    driverFilter.value = null;
    statusFilter.value = null;
    datePicked.value = !!queryDate();
    date.value = queryDate() ?? todayIn();
});
const statusOptions = ['draft', 'published', 'completed', 'cancelled'];

const routes = ref([]);
const drivers = ref([]);
const assets = ref([]);
const loading = ref(false);
const saving = ref(false);

const routeDialog = ref(false);
const stopDialog = ref(false);
const routeForm = ref({});
const stopForm = ref({});
const stopRoute = ref(null);
const stopZone = ref(null);
const zoneHint = computed(() => {
    const zone = stopZone.value ?? boardZone.value;
    return zone ? `Company time (${zone})` : 'Company time';
});

const driverOptions = computed(() => drivers.value.map((d) => ({
    title: driverName(d),
    value: d.id,
})));

const assetOptions = computed(() => assets.value.map((a) => ({title: a.label, value: a.id})));

function driverName(d) {
    const u = d?.user ?? d;
    return [u?.first_name, u?.last_name].filter(Boolean).join(' ') || `Driver ${d?.id ?? ''}`;
}

function statusColour(status) {
    return {draft: 'grey', published: 'primary', completed: 'success', cancelled: 'error'}[status] ?? 'grey';
}

function stopColour(status) {
    return {pending: 'grey', arrived: 'info', completed: 'success', failed: 'error', skipped: 'warning'}[status] ?? 'grey';
}

/**
 * Stop windows are wall-clock times in the company's zone, whatever zone the
 * browser is in. The API sends each one with that zone's offset and names the
 * zone in window_zone.
 */
function formatWindow(value, stopZoneName) {
    const zone = stopZoneName ?? boardZone.value;
    if (!zone) return formatCompanyDateTime(value, {second: undefined, timeZoneName: 'short'});
    try {
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
            hour12: true, timeZone: zone, timeZoneName: 'short',
        }).format(new Date(value));
    } catch {
        return formatCompanyDateTime(value, {second: undefined, timeZoneName: 'short'});
    }
}

function windowLabel(stop) {
    if (!stop.window_start) return '';
    const from = formatWindow(stop.window_start, stop.window_zone);
    return stop.window_end ? `${from} - ${formatWindow(stop.window_end, stop.window_zone)}` : from;
}

/** "2 of 5 done" - what the office actually wants to see at a glance. */
function progress(route) {
    const done = route.stops.filter((s) => ['completed', 'failed', 'skipped'].includes(s.status)).length;
    return route.stops.length ? `${done} of ${route.stops.length} done` : 'no stops';
}

function loadRoutes() {
    loading.value = true;
    const params = new URLSearchParams();
    if (date.value) params.append('date', date.value);
    if (driverFilter.value) params.append('driver_id', driverFilter.value);
    if (statusFilter.value) params.append('status', statusFilter.value);
    send('get', `fleet/dispatch/routes?${params.toString()}`)
        .then((response) => {
            routes.value = response.data.data.data ?? [];
            const zone = response.data.data.window_zone;
            if (zone) {
                boardZone.value = zone;
                // Until someone picks a date, the board shows the company's
                // today; changing date reloads through the watcher.
                if (!datePicked.value && date.value !== todayIn(zone)) {
                    date.value = todayIn(zone);
                }
            }
        })
        .catch(() => showSnackbar('Routes could not be loaded', 'error'))
        .finally(() => loading.value = false);
}

// Whole lists, not pages: GET assets is capped at 100 and the drivers page at 10. Both lists span
// every company for a platform admin unless told one, so an admin with no session company gets none.
function loadPickers() {
    const {user} = useAuthStore();
    const isStaff = user?.type === 'admin' || !!user?.super;
    const company = isStaff ? user?.company?.id : null;
    if (isStaff && !company) return;
    const scope = company ? {company} : {};
    send('get', 'companies/drivers', {list: true, ...scope})
        .then((r) => drivers.value = r.data.data ?? [])
        .catch(() => {});
    send('get', 'assets/list', scope)
        .then((r) => assets.value = r.data.data ?? [])
        .catch(() => {});
}

function openRouteDialog(route = null) {
    routeForm.value = route
        ? {...route, route_date: (route.route_date ?? '').slice(0, 10)}
        : {route_date: date.value, driver_id: null, asset_id: null, label: '', notes: ''};
    routeDialog.value = true;
}

function saveRoute() {
    saving.value = true;
    const body = {
        route_date: routeForm.value.route_date,
        driver_id: routeForm.value.driver_id ?? null,
        asset_id: routeForm.value.asset_id ?? null,
        label: routeForm.value.label ?? null,
        notes: routeForm.value.notes ?? null,
    };
    const request = routeForm.value.id
        ? send('put', `fleet/dispatch/routes/${routeForm.value.id}`, body)
        : send('post', 'fleet/dispatch/routes', body);
    request
        .then(() => {
            showSnackbar('Route saved', 'success');
            routeDialog.value = false;
            loadRoutes();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar('Route could not be saved', 'error'))
        .finally(() => saving.value = false);
}

function setStatus(route, status) {
    send('put', `fleet/dispatch/routes/${route.id}`, {status})
        .then(() => {
            showSnackbar(status === 'published' ? 'Published to the driver' : 'Route unpublished', 'success');
            loadRoutes();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar('Status could not be changed', 'error'));
}

function removeRoute(route) {
    if (!confirm('Delete this route and its stops?')) return;
    send('delete', `fleet/dispatch/routes/${route.id}`)
        .then(() => {
            showSnackbar('Route deleted', 'success');
            loadRoutes();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar('Route could not be deleted', 'error'));
}

function openStopDialog(route, stop = null) {
    stopRoute.value = route;
    stopZone.value = stop?.window_zone ?? route.window_zone ?? boardZone.value;
    stopForm.value = stop
        ? {
            ...stop,
            window_start: wallInput(stop.window_start, stop.window_zone),
            window_end: wallInput(stop.window_end, stop.window_zone),
        }
        : {label: '', address: '', type: 'delivery'};
    stopDialog.value = true;
}

/**
 * datetime-local wants "YYYY-MM-DDTHH:mm" with no zone, and the API reads that
 * back in the company's zone. So the form must hold the company wall time,
 * never the browser's: converting through the browser zone moved the window
 * by the browser's offset on every save.
 */
function wallInput(value, zone) {
    if (!value) return '';
    const text = String(value);
    // "2026-10-25T09:00:00-04:00": already the company wall time.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?[+-]\d{2}:\d{2}$/.test(text)) {
        return text.slice(0, 16);
    }
    const d = new Date(text);
    if (isNaN(d.getTime())) return '';
    try {
        // No zone named means an API that still stores the typed time as if
        // it were UTC, so UTC is what keeps a save from moving it.
        const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
            hourCycle: 'h23', timeZone: zone || 'UTC',
        }).formatToParts(d).map((p) => [p.type, p.value]));
        return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
    } catch {
        return '';
    }
}

function saveStop() {
    if (!stopForm.value.label) {
        showSnackbar('A stop needs a name', 'error');
        return;
    }
    saving.value = true;
    const body = {...stopForm.value};
    delete body.id;
    delete body.status;
    delete body.window_zone;
    // Zoneless on purpose: the API reads it in the company's zone.
    body.window_start = body.window_start || null;
    body.window_end = body.window_end || null;
    const request = stopForm.value.id
        ? send('put', `fleet/dispatch/stops/${stopForm.value.id}`, body)
        : send('post', `fleet/dispatch/routes/${stopRoute.value.id}/stops`, body);
    request
        .then(() => {
            showSnackbar('Stop saved', 'success');
            stopDialog.value = false;
            loadRoutes();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar('Stop could not be saved', 'error'))
        .finally(() => saving.value = false);
}

function removeStop(stop) {
    if (!confirm('Delete this stop?')) return;
    send('delete', `fleet/dispatch/stops/${stop.id}`)
        .then(() => {
            showSnackbar('Stop deleted', 'success');
            loadRoutes();
            refreshMenuCounts();
        })
        .catch(() => showSnackbar('Stop could not be deleted', 'error'));
}

/**
 * Move a stop up or down by swapping its sequence with its neighbour's.
 *
 * Two writes rather than a bulk reorder endpoint: a board is a handful of
 * stops, and a swap is the one operation that cannot leave a gap.
 */
function move(route, stop, delta) {
    const target = route.stops[route.stops.indexOf(stop) + delta];
    if (!target) return;
    // Two stops can share a number after a manual edit; a plain swap would then change nothing.
    const tie = target.sequence === stop.sequence;
    Promise.all([
        send('put', `fleet/dispatch/stops/${stop.id}`, {sequence: tie ? stop.sequence + (delta > 0 ? 1 : 0) : target.sequence}),
        send('put', `fleet/dispatch/stops/${target.id}`, {sequence: tie ? stop.sequence + (delta < 0 ? 1 : 0) : stop.sequence}),
    ])
        .then(loadRoutes)
        .catch(() => {
            showSnackbar('Could not reorder the stops', 'error');
            loadRoutes();
        });
}

watch([date, driverFilter, statusFilter], loadRoutes);

onMounted(() => {
    loadRoutes();
    loadPickers();
});
</script>
